<template>
    <b-card no-body :class="`evaluation-card ${form.evaluation.is_editing ? 'editing-border' : ''}`">
        <evaluation-header
            :evaluation="form.evaluation"
            @click="onClickEvaluationHeader"
            @accept-evaluation="onClickAcceptEvaluation"
            @decline-evaluation="onClickDeclineEvaluation"
            @enable-evaluation-edition="onClickEnableEvaluationEdition"
        />
        <transition name="fade">
            <hr v-if="form.evaluation.isOpen" style="margin-top: 1rem; margin-bottom: 1rem" />
        </transition>
        <loader :loader="form.evaluation.evaluationLoader" />
        <b-collapse v-model="form.evaluation.isOpen" :id="`evaluation-collapse-${form.evaluation.id}`">
            <transition-group name="fade">
                <div
                    :key="value.id"
                    v-bind:style="{ marginBottom: !value.$model.random ? '2rem' : '0' }"
                    v-for="value in $v.form.evaluation.values.$each.$iter"
                >
                    <subject :value="value.$model" />
                    <questions
                        :valueId="value.id.$model"
                        :evaluationId="form.evaluation.id"
                        :isEditing="form.evaluation.is_editing"
                        :questions="getValueQuestions(value.id.$model)"
                    />
                    <comments
                        :evaluationId="form.evaluation.id"
                        :isEditing="form.evaluation.is_editing"
                        :comments="getEvaluationComments(value.id.$model, 'value')"
                    />
                </div>
            </transition-group>
            <div v-if="evaluationHasGeneralComments">
                <h5>{{ $t('values.generalComments') }}</h5>
                <comments
                    :evaluationId="form.evaluation.id"
                    :isEditing="form.evaluation.is_editing"
                    :comments="getEvaluationComments(form.evaluation.questionnaire_id, 'questionnaire')"
                />
            </div>
            <div v-if="form.evaluation.is_editing && evaluationHasAnswers && !form.evaluation.questionnaire_is_closed">
                <b-button
                    id="btn-values-send-evaluation"
                    size="sm"
                    variant="primary"
                    class="float-right"
                    :disabled="form.evaluation.sendingEvaluationLoader"
                    @click="onClickSendEvaluation"
                >
                    <div v-if="form.evaluation.sendingEvaluationLoader" class="pl-2 pr-2">
                        <i class="fa fa-spinner fa-spin px-1" />
                    </div>
                    <span v-else> {{ $t('form.button.send') }} </span>
                </b-button>
            </div>
            <div v-if="evaluationHasAnswers" class="text-muted small">
                {{ $t('form.warning.requiredFields') }}
            </div>
        </b-collapse>
    </b-card>
</template>
<script>
import i18n from '@/i18n'
import Avatar from 'vue-avatar'
import validations from './validations'
import {
    sendEvaluation,
    updateEvaluation,
    acceptEvaluation,
    declineEvaluation,
    getEvaluationData,
} from '@/views/values/api'
import { EventBuzz } from '@/main'
import { success } from '@/lib/notification'
import { validationMixin } from 'vuelidate'
import { Comments, Questions, EvaluationHeader, Subject } from './components'
import Loader from '@/views/components/loader'

export default {
    name: 'Evaluation',
    mixins: [validationMixin],
    components: {
        Avatar,
        Subject,
        Comments,
        Questions,
        EvaluationHeader,
        Loader,
    },
    props: {
        evaluation: {
            type: Object,
            required: true,
        },
    },
    validations: {
        form: validations,
    },
    data() {
        return {
            form: {
                evaluation: {},
            },
        }
    },
    beforeMount() {
        this.setEvaluationForm()
    },
    methods: {
        getValueQuestions(valueId) {
            return Object.keys(this.$v.form.evaluation.answers.$each.$iter).reduce((acc, key) => {
                const question = this.$v.form.evaluation.answers.$each.$iter[key]
                if (question.$model.value_id == valueId) {
                    acc.push(question)
                }
                return acc
            }, [])
        },
        getEvaluationComments(commentableId, commentableType) {
            return Object.keys(this.$v.form.evaluation.comments.$each.$iter).reduce((acc, key) => {
                const comment = this.$v.form.evaluation.comments.$each.$iter[key]
                if (
                    comment.$model.commentable_id == commentableId &&
                    comment.$model.commentable_type == commentableType
                ) {
                    acc.push(comment)
                }
                return acc
            }, [])
        },
        onClickEvaluationHeader() {
            if (this.form.evaluation.is_editing || this.form.evaluation.status !== 'pending') {
                this.form.evaluation.isOpen = !this.form.evaluation.isOpen
                if (this.form.evaluation.isOpen && !this.evaluationHasAnswers) {
                    this.getEvaluationData()
                }
            }
        },
        onClickAcceptEvaluation() {
            this.form.evaluation.acceptingEvaluationLoader = true
            acceptEvaluation(this.form.evaluation.id)
                .then(() => {
                    this.$emit('evaluation-accepted', this.form.evaluation.id)
                    success(i18n.t('values.evaluationAccepted'))
                })
                .finally(() => {
                    this.form.evaluation.acceptingEvaluationLoader = false
                })
        },
        onClickDeclineEvaluation() {
            this.form.evaluation.decliningEvaluationLoader = true
            declineEvaluation(this.form.evaluation.id)
                .then(() => {
                    this.$emit('evaluation-declined', this.form.evaluation.id)
                    success(i18n.t('values.evaluationDeclined'))
                })
                .finally(() => {
                    this.form.evaluation.decliningEvaluationLoader = false
                })
        },
        onClickEnableEvaluationEdition() {
            this.form.evaluation.enableEvaluationEditionLoader = true
            updateEvaluation(this.form.evaluation.id, { is_editing: true })
                .then(() => {
                    this.form.evaluation.is_editing = true
                    this.form.evaluation.isOpen = true
                    if (!this.evaluationHasAnswers) {
                        this.getEvaluationData()
                    }
                    this.$emit('evaluation-edition-enabled')
                    success(i18n.t('values.evaluationEditionEnabled'))
                })
                .finally(() => {
                    this.form.evaluation.enableEvaluationEditionLoader = false
                })
        },
        scrollToInvalidField() {
            const elements = document.getElementsByClassName('has-error')
            if (elements.length) {
                const item = elements[0]
                const top = item.getBoundingClientRect().top + window.pageYOffset - 100
                window.scrollTo({ top, behavior: 'smooth' })
            }
        },
        onClickSendEvaluation() {
            this.$v.$touch()
            if (this.$v.form.evaluation.$invalid) {
                return this.$nextTick(this.scrollToInvalidField)
            }

            this.form.evaluation.sendingEvaluationLoader = true
            sendEvaluation(this.form.evaluation.id)
                .then(() => {
                    EventBuzz.$emit('evaluation-sented')
                    success(i18n.t('values.evaluationSent'))
                    this.$emit('evaluation-sented', this.form.evaluation.id)

                    this.form.evaluation.status = 'sent'
                    this.form.evaluation.is_editing = false
                })
                .finally(() => {
                    this.form.evaluation.sendingEvaluationLoader = false
                })
        },
        getEvaluationData() {
            this.form.evaluation.evaluationLoader = true
            getEvaluationData(this.form.evaluation.id)
                .then((response) => {
                    this.setEvaluationDataForm(response.data.questionnaire)
                })
                .finally(() => {
                    this.form.evaluation.evaluationLoader = false
                })
        },
        setEvaluationDataForm(data) {
            const values = this.parseValues(data)
            const comments = this.parseComments(data)
            const answers = this.parseAnswers(data)

            this.form.evaluation = {
                ...this.form.evaluation,
                values,
                comments,
                answers,
            }
        },
        parseValues(data) {
            const values = data.values.map((value) => {
                return {
                    id: value.id,
                    title: value.title,
                    picture: value.picture,
                    description: value.description,
                    random: data.random,
                }
            })
            return values
        },
        parseComments(data) {
            let comments = data.values.reduce((acc, value) => {
                const valueComments = value.comments.map((comment) => {
                    return {
                        ...comment,
                        value_id: value.id,
                        commentable_id: value.id,
                        commentable_type: 'value',
                    }
                })

                return acc.concat(valueComments)
            }, [])
            comments = comments.concat(
                data.comments.map((comment) => {
                    return {
                        ...comment,
                        commentable_id: data.id,
                        commentable_type: 'questionnaire',
                    }
                })
            )
            return comments
        },
        parseAnswers(data) {
            const answers = data.values.reduce((acc, value) => {
                const questions = value.questions.map((question) => {
                    return {
                        id: question.id,
                        value_id: value.id,
                        not_able_to_answer: false,
                        degrees: question.degrees,
                        score: question.score.score,
                        score_id: question.score.id,
                        scale_id: question.scale_id,
                        is_nullable: question.is_nullable,
                        is_required: question.is_required,
                        degree_id: question.score.degree_id,
                        scale_format: question.scale_format,
                        key_behavior_id: question.key_behavior.id,
                        key_behavior_title: question.key_behavior.title,
                    }
                })

                return acc.concat(questions)
            }, [])
            return answers
        },
        setEvaluationForm() {
            const { id, type, status, evaluated, is_editing, questionnaire } = this.evaluation
            this.form.evaluation = {
                id,
                type,
                status,
                answers: [],
                comments: [],
                isOpen: false,
                can_edit: questionnaire.allow_evaluation_edition,
                evaluated,
                is_editing,
                can_decline: questionnaire[`allow_${type}_evaluation_declination`],
                questionnaire_id: questionnaire.id,
                evaluationLoader: false,
                sendingEvaluationLoader: false,
                decliningEvaluationLoader: false,
                acceptingEvaluationLoader: false,
                enableEvaluationEditionLoader: false,
                questionnaire_title: questionnaire.title,
                questionnaire_is_closed: questionnaire.closed_at,
            }
        },
    },
    computed: {
        evaluationHasAnswers() {
            return this.form.evaluation.answers.length > 0 || this.form.evaluation.comments.length > 0
        },
        evaluationHasGeneralComments() {
            return this.form.evaluation.comments.find((comment) => comment.commentable_type === 'questionnaire')
        },
    },
}
</script>
<style lang="sass" scoped>
$primary-color: #20a8d8

.text-primary
    color: $primary-color

.evaluation-card
    padding: 20px
    margin-bottom: 20px
    border-radius: 0.4rem
    box-shadow: 2px 2px rgb(235,235,235)

.editing-border
    border: 2px solid #FFD380
    box-shadow: 3px 3px rgb(235,235,235)

.fade-enter-active,
.fade-leave-active
    transition: opacity .25s

.fade-enter,
.fade-leave-to
    opacity: 0
</style>
